// store global types
import type { TCreateSlice, TAsyncState } from "@/store";

// dependencies types

import type { TApiAuth } from "@/apis/auth/apiAuth";

import type {
    TAllowedOperatingStatementPeriods,
    TApiThirdParty,
    TErrorGetThirdPartyChartOfAccounts,
    TErrorGetThirdPartyChartOfAccountsById,
    TErrorGetThirdPartyDatabases,
    TErrorGetThirdPartyDeals,
    TErrorGetThirdPartyDealsByDealIdOperatingStatement,
    TErrorGetThirdPartyDealsByDealIdPeriods,
    TErrorGetThirdPartyDealsByDealNameOperatingStatement,
    TErrorGetThirdPartyProjectsByProjectIdOperatingStatement,
    TErrorGetThirdPartyProjectsByProjectIdPeriods,
    TResponseGetThirdPartyChartOfAccounts,
    TResponseGetThirdPartyChartOfAccountsById,
    TResponseGetThirdPartyDatabases,
    TResponseGetThirdPartyDeals,
    TResponseGetThirdPartyDealsByDealIdOperatingStatement,
    TResponseGetThirdPartyDealsByDealIdPeriods,
    TResponseGetThirdPartyDealsByDealNameOperatingStatement,
    TResponseGetThirdPartyProjectsByProjectIdOperatingStatement,
    TResponseGetThirdPartyProjectsByProjectIdPeriods,
} from "@/apis/apiThirdParty/apiThirdParty";
import { type TSliceNotifications } from "../notifications/sliceNotifications";
import { type TSliceThirdPartyAuth } from "../thirdpartyAuth/sliceThirdpartyAuth";

type TStateThirdPartyDoc = {
    databaseListResponse: TAsyncState<
        TResponseGetThirdPartyDatabases,
        TErrorGetThirdPartyDatabases
    >;
    dealListResponse: TAsyncState<
        TResponseGetThirdPartyDeals,
        TErrorGetThirdPartyDeals
    >;
    getOperatingStatementByIdResponse: TAsyncState<
        TResponseGetThirdPartyDealsByDealIdOperatingStatement,
        TErrorGetThirdPartyDealsByDealIdOperatingStatement
    >;
    getOperatingStatementByNameResponse: TAsyncState<
        TResponseGetThirdPartyDealsByDealNameOperatingStatement,
        TErrorGetThirdPartyDealsByDealNameOperatingStatement
    >;
    projectPeriodsResponse: TAsyncState<
        TResponseGetThirdPartyProjectsByProjectIdPeriods,
        TErrorGetThirdPartyProjectsByProjectIdPeriods
    >;
    projectOperatingStatementResponse: TAsyncState<
        TResponseGetThirdPartyProjectsByProjectIdOperatingStatement,
        TErrorGetThirdPartyProjectsByProjectIdOperatingStatement
    >;
    dealListRequestDatabaseIds: string | null;
    dealPeriodsRequestDealId: number | null;
    projectPeriodsRequestProjectId: number | null;
    projectPeriodsRequestDatabaseId: number | null;
    operatingStatementRequestDealId: number | null;
    operatingStatementRequestDealName: string | null;
    projectOperatingStatementRequestProjectId: number | null;
    projectOperatingStatementRequestDatabaseId: number | null;
    dealPeriodsResponse: TAsyncState<
        TResponseGetThirdPartyDealsByDealIdPeriods,
        TErrorGetThirdPartyDealsByDealIdPeriods
    >;
    operatingStatementDetailed: boolean;
    projectOperatingStatementDetailed: boolean;
    operatingStatementRequestFromDate: string | null;
    projectOperatingStatementRequestFromDate: string | null;
    operatingStatementRequestToDate: string | null;
    projectOperatingStatementRequestToDate: string | null;
    operatingStatementRequestAggregationInterval: TAllowedOperatingStatementPeriods | null;
    operatingStatementLookupType: "id" | "name";
    operatingStatementLookupValue: string | null;
    projectOperatingStatementRequestAggregationInterval: TAllowedOperatingStatementPeriods | null;
    tokenVisible: boolean;
    coaListResponse: TAsyncState<
        TResponseGetThirdPartyChartOfAccounts,
        TErrorGetThirdPartyChartOfAccounts
    >;
    coaDetailResponse: TAsyncState<
        TResponseGetThirdPartyChartOfAccountsById,
        TErrorGetThirdPartyChartOfAccountsById
    >;
    coaListRequestDatabaseIds: string | null;
    coaDetailRequestCoaId: number | null;
};
type TActionsPublicThirdPartyDoc = {
    resetDatabaseListResponse: () => void;
    resetDealListResponse: () => void;
    resetDealPeriodsResponse: () => void;
    resetOperatingStatementByIdResponse: () => void;
    resetOperatingStatementByNameResponse: () => void;
    resetProjectPeriodsResponse: () => void;
    resetProjectOperatingStatementResponse: () => void;
    setOperatingStatementDetailed: (detailed: boolean) => void;
    setProjectOperatingStatementDetailed: (detailed: boolean) => void;
    setDealListRequestDatabaseIds: (databaseIds: string) => void;
    setDealPeriodsRequestDealId: (dealId: number) => void;
    setOperatingStatementRequestDealId: (dealId: number) => void;
    setProjectPeriodsRequestProjectId: (projectId: number) => void;
    setProjectPeriodsRequestDatabaseId: (databaseId: number) => void;
    setProjectOperatingStatementRequestProjectId: (projectId: number) => void;
    setProjectOperatingStatementRequestDatabaseId: (databaseId: number) => void;
    handleLoginButtonClicked: () => Promise<void>;
    handleLogoutButtonClicked: () => Promise<void>;
    handleRequestDatabases: () => Promise<void>;
    handleRequestDeals: () => Promise<void>;
    handleRequestDealPeriods: () => Promise<void>;
    handleRequestProjectPeriods: () => Promise<void>;
    handleRequestOperatingStatementById: () => Promise<void>;
    handleRequestOperatingStatementByName: () => Promise<void>;
    handleRequestProjectOperatingStatement: () => Promise<void>;
    toggleToken: () => Promise<void>;
    copyToken: () => Promise<void>;
    setOperatingStatementRequestFromDate: (fromDate: string) => void;
    setProjectOperatingStatementRequestFromDate: (fromDate: string) => void;
    setOperatingStatementRequestToDate: (toDate: string) => void;
    setProjectOperatingStatementRequestToDate: (toDate: string) => void;
    setOperatingStatementRequestAggregationInterval: (
        interval: TAllowedOperatingStatementPeriods,
    ) => void;
    setOperatingStatementLookupType: (type: "id" | "name") => void;
    setOperatingStatementLookupValue: (value: string) => void;
    handleRequestOperatingStatementByLookup: () => Promise<void>;
    setProjectOperatingStatementRequestAggregationInterval: (
        interval: TAllowedOperatingStatementPeriods,
    ) => void;
    handleAuthRedirectAndFetchUser: () => Promise<void>;
    handleRequestChartOfAccountsList: () => Promise<void>;
    handleRequestChartOfAccountsById: () => Promise<void>;
    setCoaListRequestDatabaseIds: (databaseIds: string) => void;
    setCoaDetailRequestCoaId: (coaId: number) => void;
    resetCoaListResponse: () => void;
    resetCoaDetailResponse: () => void;
};

export type TSliceThirdPartyDoc = TStateThirdPartyDoc &
    TActionsPublicThirdPartyDoc;

type TSliceDependenciesThirdPartyDoc = {
    apiThirdParty: TApiThirdParty;
    apiThirdPartyAuth: TApiAuth;
    navigateToReferer: ({ url }: { url: string | null }) => void;
    addNotificationWithDefaultTimeout: TSliceNotifications["addNotificationWithDefaultTimeout"];
    getAccessToken: TSliceThirdPartyAuth["getThirdPartyTokenOrRedirectToLogin"];
    onInitializeSuccess: TSliceThirdPartyAuth["onInitializeSuccess"];
    getUser: TSliceThirdPartyAuth["getUser"];
    logoutThirdParty: TSliceThirdPartyAuth["logoutThirdParty"];
};

export const initialStateThirdPartyDoc = {
    projectPeriodsResponse: {
        status: "idle",
        data: null,
        error: null,
    },
    projectOperatingStatementResponse: {
        status: "idle",
        data: null,
        error: null,
    },
    databaseListResponse: {
        status: "idle",
        data: null,
        error: null,
    },
    dealListResponse: {
        status: "idle",
        data: null,
        error: null,
    },
    getOperatingStatementByIdResponse: {
        status: "idle",
        data: null,
        error: null,
    },
    getOperatingStatementByNameResponse: {
        status: "idle",
        data: null,
        error: null,
    },
    projectPeriodsRequestProjectId: null,
    projectPeriodsRequestDatabaseId: null,
    projectOperatingStatementRequestProjectId: null,
    projectOperatingStatementRequestDatabaseId: null,
    dealListRequestDatabaseIds: null,
    dealPeriodsRequestDealId: null,
    operatingStatementRequestDealId: null,
    operatingStatementRequestDealName: null,
    dealPeriodsResponse: {
        status: "idle",
        data: null,
        error: null,
    },
    operatingStatementDetailed: false,
    operatingStatementRequestFromDate: null,
    operatingStatementRequestToDate: null,
    operatingStatementRequestAggregationInterval: null,
    operatingStatementLookupType: "id",
    operatingStatementLookupValue: null,
    projectOperatingStatementRequestAggregationInterval: null,
    projectOperatingStatementDetailed: false,
    projectOperatingStatementRequestFromDate: null,
    projectOperatingStatementRequestToDate: null,
    tokenVisible: false,
    coaListResponse: {
        status: "idle",
        data: null,
        error: null,
    },
    coaDetailResponse: {
        status: "idle",
        data: null,
        error: null,
    },
    coaListRequestDatabaseIds: null,
    coaDetailRequestCoaId: null,
} as const satisfies TStateThirdPartyDoc;

export const createSliceThirdPartyDoc: TCreateSlice<
    TSliceThirdPartyDoc,
    TSliceDependenciesThirdPartyDoc
> =
    ({
        apiThirdPartyAuth,
        apiThirdParty,
        navigateToReferer,
        addNotificationWithDefaultTimeout,
        getAccessToken,
        getUser,
        logoutThirdParty,
    }) =>
    (set, get) => {
        const actionsPublic: TActionsPublicThirdPartyDoc = {
            handleAuthRedirectAndFetchUser: async () => {
                const query = window.location.search;
                if (query.includes("code=") && query.includes("state=")) {
                    const returnTo =
                        await apiThirdPartyAuth.handleRedirectCallback();
                    if (returnTo !== undefined) {
                        navigateToReferer({ url: returnTo });
                    }
                    window.history.replaceState(
                        {},
                        document.title,
                        window.location.pathname,
                    );
                }

                void getUser();
            },
            setOperatingStatementRequestAggregationInterval: (
                interval: TAllowedOperatingStatementPeriods,
            ) => {
                set({
                    operatingStatementRequestAggregationInterval: interval,
                });
            },
            setOperatingStatementLookupType: (type: "id" | "name") => {
                set({ operatingStatementLookupType: type });
            },
            setOperatingStatementLookupValue: (value: string) => {
                set({ operatingStatementLookupValue: value });
            },
            handleRequestOperatingStatementByLookup: async () => {
                const lookupType = get().operatingStatementLookupType;
                const lookupValue = get().operatingStatementLookupValue;

                if (lookupValue === null) return;

                if (lookupType === "id") {
                    const dealId = parseInt(lookupValue, 10);
                    if (!isNaN(dealId)) {
                        set({ operatingStatementRequestDealId: dealId });
                        await actionsPublic.handleRequestOperatingStatementById();
                    } else {
                        // If ID is not numeric, treat it as a name
                        set({ operatingStatementRequestDealName: lookupValue });
                        await actionsPublic.handleRequestOperatingStatementByName();
                    }
                } else {
                    set({ operatingStatementRequestDealName: lookupValue });
                    await actionsPublic.handleRequestOperatingStatementByName();
                }
            },
            setProjectOperatingStatementRequestAggregationInterval: (
                interval: TAllowedOperatingStatementPeriods,
            ) => {
                set({
                    projectOperatingStatementRequestAggregationInterval:
                        interval,
                });
            },
            setOperatingStatementRequestFromDate: (fromDate: string) => {
                set({ operatingStatementRequestFromDate: fromDate });
            },
            setProjectOperatingStatementRequestFromDate: (fromDate: string) => {
                set({ projectOperatingStatementRequestFromDate: fromDate });
            },
            setOperatingStatementRequestToDate: (toDate: string) => {
                set({ operatingStatementRequestToDate: toDate });
            },
            setProjectOperatingStatementRequestToDate: (toDate: string) => {
                set({ projectOperatingStatementRequestToDate: toDate });
            },
            setOperatingStatementDetailed: (detailed: boolean) => {
                set({ operatingStatementDetailed: detailed });
            },
            setProjectOperatingStatementDetailed: (detailed: boolean) => {
                set({ projectOperatingStatementDetailed: detailed });
            },
            setDealListRequestDatabaseIds: (databaseIds: string) => {
                set({ dealListRequestDatabaseIds: databaseIds });
            },
            setDealPeriodsRequestDealId: (dealId: number) => {
                set({ dealPeriodsRequestDealId: dealId });
            },
            setOperatingStatementRequestDealId: (dealId: number) => {
                set({ operatingStatementRequestDealId: dealId });
            },
            setProjectPeriodsRequestProjectId: (projectId: number) => {
                set({ projectPeriodsRequestProjectId: projectId });
            },
            setProjectPeriodsRequestDatabaseId: (databaseId: number) => {
                set({ projectPeriodsRequestDatabaseId: databaseId });
            },
            setProjectOperatingStatementRequestProjectId: (
                projectId: number,
            ) => {
                set({ projectOperatingStatementRequestProjectId: projectId });
            },
            setProjectOperatingStatementRequestDatabaseId: (
                databaseId: number,
            ) => {
                set({ projectOperatingStatementRequestDatabaseId: databaseId });
            },
            resetDatabaseListResponse: () => {
                set({
                    databaseListResponse: {
                        status: "idle",
                        data: null,
                        error: null,
                    },
                });
            },
            resetDealListResponse: () => {
                set({
                    dealListResponse: {
                        status: "idle",
                        data: null,
                        error: null,
                    },
                    dealListRequestDatabaseIds: null,
                });
            },
            resetDealPeriodsResponse: () => {
                set({
                    dealPeriodsResponse: {
                        status: "idle",
                        data: null,
                        error: null,
                    },
                    dealPeriodsRequestDealId: null,
                });
            },
            resetProjectPeriodsResponse: () => {
                set({
                    projectPeriodsResponse: {
                        status: "idle",
                        data: null,
                        error: null,
                    },
                    projectPeriodsRequestProjectId: null,
                });
            },
            resetOperatingStatementByIdResponse: () => {
                set({
                    getOperatingStatementByIdResponse: {
                        status: "idle",
                        data: null,
                        error: null,
                    },
                    operatingStatementRequestDealId: null,
                    operatingStatementRequestFromDate: null,
                    operatingStatementRequestToDate: null,
                    operatingStatementRequestAggregationInterval: null,
                });
            },
            resetOperatingStatementByNameResponse: () => {
                set({
                    getOperatingStatementByNameResponse: {
                        status: "idle",
                        data: null,
                        error: null,
                    },
                });
            },
            resetProjectOperatingStatementResponse: () => {
                set({
                    projectOperatingStatementResponse: {
                        status: "idle",
                        data: null,
                        error: null,
                    },
                    projectOperatingStatementRequestProjectId: null,
                    projectOperatingStatementRequestFromDate: null,
                    projectOperatingStatementRequestToDate: null,
                    projectOperatingStatementRequestAggregationInterval: null,
                });
            },
            handleLoginButtonClicked: async () => {
                await apiThirdPartyAuth.loginWithRedirect();
                await actionsPublic.handleAuthRedirectAndFetchUser();
            },
            handleLogoutButtonClicked: async () => {
                try {
                    await logoutThirdParty();
                    addNotificationWithDefaultTimeout({
                        kind: "toast",
                        title: "Logged out",
                        severity: "success",
                    });
                } catch {
                    addNotificationWithDefaultTimeout({
                        kind: "toast",
                        title: "Failed to logout",
                        severity: "danger",
                    });
                }
            },
            handleRequestDatabases: async () => {
                set(
                    {
                        databaseListResponse: {
                            status: "pending",
                            data: null,
                            error: null,
                        },
                    },
                    false,
                    "thirdpartydoc/handleRequestDatabases/pending",
                );
                try {
                    const response = await apiThirdParty.getDatabases();
                    set(
                        {
                            databaseListResponse: {
                                status: "resolved",
                                data: response.data,
                                error: null,
                            },
                        },
                        false,
                        "thirdpartydoc/handleRequestDatabases/resolved",
                    );
                } catch (error) {
                    set(
                        {
                            databaseListResponse: {
                                status: "rejected",
                                data: null,
                                error: error as TErrorGetThirdPartyDatabases,
                            },
                        },
                        false,
                        "thirdpartydoc/handleRequestDatabases/rejected",
                    );
                }
            },
            handleRequestDeals: async () => {
                set(
                    {
                        dealListResponse: {
                            status: "pending",
                            data: null,
                            error: null,
                        },
                    },
                    false,
                    "thirdpartydoc/handleRequestDeals/pending",
                );
                const dealListRequestDatabaseIds =
                    get().dealListRequestDatabaseIds;
                if (dealListRequestDatabaseIds === null) return;
                try {
                    const response = await apiThirdParty.getDeals(
                        dealListRequestDatabaseIds.toString(),
                    );
                    set(
                        {
                            dealListResponse: {
                                status: "resolved",
                                data: response.data,
                                error: null,
                            },
                        },
                        false,
                        "thirdpartydoc/handleRequestDeals/resolved",
                    );
                } catch (error) {
                    set(
                        {
                            dealListResponse: {
                                status: "rejected",
                                data: null,
                                error: error as TErrorGetThirdPartyDeals,
                            },
                        },
                        false,
                        "thirdpartydoc/handleRequestDeals/rejected",
                    );
                }
            },
            handleRequestDealPeriods: async () => {
                set(
                    {
                        dealPeriodsResponse: {
                            status: "pending",
                            data: null,
                            error: null,
                        },
                    },
                    false,
                    "thirdpartydoc/handleRequestDealPeriods/pending",
                );
                try {
                    const dealId = get().dealPeriodsRequestDealId;
                    if (dealId === null) return;
                    const response =
                        await apiThirdParty.getDealsByDealIdPeriods({
                            dealId,
                        });
                    set(
                        {
                            dealPeriodsResponse: {
                                status: "resolved",
                                data: response.data,
                                error: null,
                            },
                        },
                        false,
                        "thirdpartydoc/handleRequestDealPeriods/resolved",
                    );
                } catch (error) {
                    set(
                        {
                            dealPeriodsResponse: {
                                status: "rejected",
                                data: null,
                                error: error as TErrorGetThirdPartyDealsByDealIdPeriods,
                            },
                        },
                        false,
                        "thirdpartydoc/handleRequestDealPeriods/resolved",
                    );
                }
            },
            handleRequestProjectPeriods: async () => {
                set(
                    {
                        projectPeriodsResponse: {
                            status: "pending",
                            data: null,
                            error: null,
                        },
                    },
                    false,
                    "thirdpartydoc/handleRequestProjectPeriods/pending",
                );
                const projectId = get().projectPeriodsRequestProjectId;
                const databaseId = get().projectPeriodsRequestDatabaseId;
                if (projectId === null || databaseId === null) return;
                try {
                    const response =
                        await apiThirdParty.getDealsByProjectIdPeriods({
                            projectId,
                            databaseId,
                        });
                    set(
                        {
                            projectPeriodsResponse: {
                                status: "resolved",
                                data: response.data,
                                error: null,
                            },
                        },
                        false,
                        "thirdpartydoc/handleRequestProjectPeriods/resolved",
                    );
                } catch (error) {
                    set(
                        {
                            projectPeriodsResponse: {
                                status: "rejected",
                                data: null,
                                error: error as TErrorGetThirdPartyProjectsByProjectIdPeriods,
                            },
                        },
                        false,
                        "thirdpartydoc/handleRequestProjectPeriods/rejected",
                    );
                }
            },
            handleRequestOperatingStatementById: async () => {
                set(
                    {
                        getOperatingStatementByIdResponse: {
                            status: "pending",
                            data: null,
                            error: null,
                        },
                    },
                    false,
                    "thirdpartydoc/handleRequestOperatingStatementById/pending",
                );
                const dealId = get().operatingStatementRequestDealId;
                if (dealId === null) return;
                try {
                    const response =
                        await apiThirdParty.getDealsByDealIdOperatingStatement({
                            dealId,
                            detailed: get().operatingStatementDetailed,
                            startDate:
                                get().operatingStatementRequestFromDate ??
                                undefined,
                            endDate:
                                get().operatingStatementRequestToDate ??
                                undefined,
                            aggregationInterval:
                                get()
                                    .operatingStatementRequestAggregationInterval ??
                                undefined,
                        });
                    set(
                        {
                            getOperatingStatementByIdResponse: {
                                status: "resolved",
                                data: response.data,
                                error: null,
                            },
                        },
                        false,
                        "thirdpartydoc/handleRequestOperatingStatementById/resolved",
                    );
                } catch (error) {
                    set(
                        {
                            getOperatingStatementByIdResponse: {
                                status: "rejected",
                                data: null,
                                error: error as TErrorGetThirdPartyDealsByDealIdOperatingStatement,
                            },
                        },
                        false,
                        "thirdpartydoc/handleRequestOperatingStatementById/rejected",
                    );
                }
            },
            handleRequestOperatingStatementByName: async () => {
                set(
                    {
                        getOperatingStatementByNameResponse: {
                            status: "pending",
                            data: null,
                            error: null,
                        },
                    },
                    false,
                    "thirdpartydoc/handleRequestOperatingStatementByName/pending",
                );
                const dealName = get().operatingStatementRequestDealName;
                if (dealName === null) return;
                try {
                    const response =
                        await apiThirdParty.getDealsByDealNameOperatingStatement(
                            {
                                dealName,
                                detailed: get().operatingStatementDetailed,
                                startDate:
                                    get().operatingStatementRequestFromDate ??
                                    undefined,
                                endDate:
                                    get().operatingStatementRequestToDate ??
                                    undefined,
                                aggregationInterval:
                                    get()
                                        .operatingStatementRequestAggregationInterval ??
                                    undefined,
                            },
                        );
                    set(
                        {
                            getOperatingStatementByNameResponse: {
                                status: "resolved",
                                data: response.data,
                                error: null,
                            },
                        },
                        false,
                        "thirdpartydoc/handleRequestOperatingStatementByName/resolved",
                    );
                } catch (error) {
                    set(
                        {
                            getOperatingStatementByNameResponse: {
                                status: "rejected",
                                data: null,
                                error: error as TErrorGetThirdPartyDealsByDealNameOperatingStatement,
                            },
                        },
                        false,
                        "thirdpartydoc/handleRequestOperatingStatementByName/rejected",
                    );
                }
            },
            handleRequestProjectOperatingStatement: async () => {
                set(
                    {
                        projectOperatingStatementResponse: {
                            status: "pending",
                            data: null,
                            error: null,
                        },
                    },
                    false,
                    "thirdpartydoc/handleRequestProjectOperatingStatement/pending",
                );
                const projectId =
                    get().projectOperatingStatementRequestProjectId;
                const databaseId =
                    get().projectOperatingStatementRequestDatabaseId;
                if (projectId === null || databaseId === null) return;
                try {
                    const response =
                        await apiThirdParty.getDealsByProjectIdOperatingStatement(
                            {
                                projectId,
                                databaseId,
                                detailed:
                                    get().projectOperatingStatementDetailed,
                                startDate:
                                    get()
                                        .projectOperatingStatementRequestFromDate ??
                                    undefined,
                                endDate:
                                    get()
                                        .projectOperatingStatementRequestToDate ??
                                    undefined,
                                aggregationInterval:
                                    get()
                                        .projectOperatingStatementRequestAggregationInterval ??
                                    undefined,
                            },
                        );
                    set(
                        {
                            projectOperatingStatementResponse: {
                                status: "resolved",
                                data: response.data,
                                error: null,
                            },
                        },
                        false,
                        "thirdpartydoc/handleRequestProjectOperatingStatement/resolved",
                    );
                } catch (error) {
                    set(
                        {
                            projectOperatingStatementResponse: {
                                status: "rejected",
                                data: null,
                                error: error as TErrorGetThirdPartyProjectsByProjectIdOperatingStatement,
                            },
                        },
                        false,
                        "thirdpartydoc/handleRequestProjectOperatingStatement/rejected",
                    );
                }
            },
            setCoaListRequestDatabaseIds: (databaseIds: string) => {
                set({ coaListRequestDatabaseIds: databaseIds });
            },
            setCoaDetailRequestCoaId: (coaId: number) => {
                set({ coaDetailRequestCoaId: coaId });
            },
            resetCoaListResponse: () => {
                set({
                    coaListResponse: {
                        status: "idle",
                        data: null,
                        error: null,
                    },
                    coaListRequestDatabaseIds: null,
                });
            },
            resetCoaDetailResponse: () => {
                set({
                    coaDetailResponse: {
                        status: "idle",
                        data: null,
                        error: null,
                    },
                    coaDetailRequestCoaId: null,
                });
            },
            handleRequestChartOfAccountsList: async () => {
                set(
                    {
                        coaListResponse: {
                            status: "pending",
                            data: null,
                            error: null,
                        },
                    },
                    false,
                    "thirdpartydoc/handleRequestChartOfAccountsList/pending",
                );
                const databaseIds = get().coaListRequestDatabaseIds;
                try {
                    const response = await apiThirdParty.getChartOfAccounts(
                        databaseIds ?? undefined,
                    );
                    set(
                        {
                            coaListResponse: {
                                status: "resolved",
                                data: response.data,
                                error: null,
                            },
                        },
                        false,
                        "thirdpartydoc/handleRequestChartOfAccountsList/resolved",
                    );
                } catch (error) {
                    set(
                        {
                            coaListResponse: {
                                status: "rejected",
                                data: null,
                                error: error as TErrorGetThirdPartyChartOfAccounts,
                            },
                        },
                        false,
                        "thirdpartydoc/handleRequestChartOfAccountsList/rejected",
                    );
                }
            },
            handleRequestChartOfAccountsById: async () => {
                set(
                    {
                        coaDetailResponse: {
                            status: "pending",
                            data: null,
                            error: null,
                        },
                    },
                    false,
                    "thirdpartydoc/handleRequestChartOfAccountsById/pending",
                );
                const coaId = get().coaDetailRequestCoaId;
                if (coaId === null) return;
                try {
                    const response = await apiThirdParty.getChartOfAccountsById(
                        { coaId },
                    );
                    set(
                        {
                            coaDetailResponse: {
                                status: "resolved",
                                data: response.data,
                                error: null,
                            },
                        },
                        false,
                        "thirdpartydoc/handleRequestChartOfAccountsById/resolved",
                    );
                } catch (error) {
                    set(
                        {
                            coaDetailResponse: {
                                status: "rejected",
                                data: null,
                                error: error as TErrorGetThirdPartyChartOfAccountsById,
                            },
                        },
                        false,
                        "thirdpartydoc/handleRequestChartOfAccountsById/rejected",
                    );
                }
            },
            toggleToken: async () => {
                if (get().tokenVisible) {
                    set({ tokenVisible: false });
                    return;
                }
                try {
                    await getAccessToken();
                    set({ tokenVisible: true });
                } catch {
                    addNotificationWithDefaultTimeout({
                        kind: "toast",
                        title: "Failed to get token",
                        severity: "danger",
                    });
                }
            },
            copyToken: async () => {
                try {
                    const accessToken = await getAccessToken();
                    if (accessToken === null || accessToken === undefined)
                        throw new Error("Failed to get access token");
                    await navigator.clipboard
                        .writeText(accessToken)
                        .then(() => {
                            addNotificationWithDefaultTimeout({
                                kind: "toast",
                                title: "Access token copied to clipboard!",
                                severity: "success",
                            });
                        });
                } catch {
                    addNotificationWithDefaultTimeout({
                        kind: "toast",
                        title: "Failed to copy token to clipboard",
                        severity: "danger",
                    });
                }
            },
        };

        return {
            ...initialStateThirdPartyDoc,
            ...actionsPublic,
        };
    };
